/**
 * KHO NHẠC — phần logic thuần. Khác mọi mục khác: dữ liệu không nằm trong bootstrap và không lọc
 * theo NGƯỜI nhập mà theo ĐƠN VỊ (máy chủ lọc sẵn) — mỗi thư mục giao cho một nhóm, hoặc cho CẢ một
 * văn phòng (chốt 23/09/2026).
 */
import {
  MUSIC_MIME_BY_EXTENSION,
  type MusicFolderDto,
  type MusicLibraryResponse,
  type MusicTrackDto,
  type OrgUnitDto,
} from '@halo/shared';
import { formatFullDate, today } from './dates';
import { normalizeForSearch } from './format';
import { selectOptionMatches } from './select-search';

// ---------------------------------------------------------------- hiển thị

const KB = 1024;
const MB = KB * 1024;
const GB = MB * 1024;
const decimal = (value: number, digits: number) => value.toFixed(digits).replace('.', ',');

/** B · KB (tròn) · MB (1 số lẻ) như bản cũ; thêm GB vì TỔNG cả kho vượt 1 GB rất nhanh. */
export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  if (bytes < KB) return `${Math.round(bytes)} B`;
  if (bytes < MB) return `${decimal(bytes / KB, 0)} KB`;
  if (bytes < GB) return `${decimal(bytes / MB, 1)} MB`;
  return `${decimal(bytes / GB, 2)} GB`;
}

/** Trần một file như máy chủ nói: «4 GB» khi chẵn GB, còn lại «200 MB»; '' khi chưa biết trần. */
export function describeUploadLimit(maxFileBytes: number): string {
  if (!(maxFileBytes > 0)) return '';
  const mb = Math.floor(maxFileBytes / MB);
  return mb % 1024 === 0 ? `${mb / 1024} GB` : `${mb} MB`;
}

/** Ngày đưa lên «DD/MM/YYYY» theo giờ máy người dùng; chuỗi hỏng → rỗng. */
export function formatUploadDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : formatFullDate(today(date));
}

/** Thư mục giao cho CẢ VĂN PHÒNG thì nhãn ghi rõ như vậy — «Cả Phòng KD06». */
export const wholeOfficeLabel = (departmentName: string) => `Cả ${departmentName}`;

/**
 * Tên đơn vị nhận thư mục, KÈM tên phòng khi là nhóm: công ty có «Nhóm Love Songs» ở hai phòng khác
 * nhau, tên trần thì người đưa nhạc lên không biết mình đang giao thư mục cho nhóm nào.
 *
 * PHÒNG ĐỨNG TRƯỚC, NHÓM ĐỨNG SAU — «Phòng KD01 · Nhóm Cha Cha» (đổi 24/09/2026, trước đó ngược
 * lại): đọc từ lớn xuống nhỏ đúng như sơ đồ công ty, và các nhãn xếp cạnh nhau thì cùng bắt đầu
 * bằng tên phòng nên gom mắt theo phòng được ngay.
 *
 * Đơn vị là một VĂN PHÒNG (chốt 23/09/2026) thì ghi «Cả …». Mã lạ (đơn vị đã xoá) → chính mã đó.
 */
export function teamLabeller(orgUnits: readonly OrgUnitDto[]): (unitId: string) => string {
  const byId = new Map(orgUnits.map((unit) => [unit.id, unit]));
  return (unitId) => {
    const unit = byId.get(unitId);
    if (!unit) return unitId || '—';
    if (unit.kind === 'department') return wholeOfficeLabel(unit.name);
    const department = unit.parentId ? byId.get(unit.parentId) : undefined;
    return department ? `${department.name} · ${unit.name}` : unit.name;
  };
}

const byName = new Intl.Collator('vi', { numeric: true, sensitivity: 'base' });

export interface TeamChoice {
  value: string;
  label: string;
  /** Chữ để tìm: tên nhóm KÈM tên phòng, để gõ «love kd06» ra đúng một nhóm. */
  search: string;
}
export interface TeamChoiceGroup {
  label: string;
  options: TeamChoice[];
}

/**
 * Ô «Giao cho»: mỗi phòng một khối — đầu khối là «Cả <phòng>» (giao cho TOÀN VĂN PHÒNG, chốt
 * 23/09/2026), dưới là từng nhóm xếp theo tên. Phòng không có nhóm nào vẫn hiện, vì giao cho cả
 * phòng luôn làm được.
 */
export function teamChoices(orgUnits: readonly OrgUnitDto[]): TeamChoiceGroup[] {
  const teams = orgUnits.filter((unit) => unit.kind === 'team');
  const departments = orgUnits
    .filter((unit) => unit.kind === 'department')
    .sort((a, b) => byName.compare(a.name, b.name));
  const optionsOf = (list: OrgUnitDto[], departmentName: string): TeamChoice[] =>
    list
      .sort((a, b) => byName.compare(a.name, b.name))
      .map((team) => ({
        value: team.id,
        label: team.name,
        search: `${team.name} ${departmentName}`.trim(),
      }));

  const known = new Set(departments.map((department) => department.id));
  const groups = departments.map((department) => ({
    label: department.name,
    options: [
      {
        value: department.id,
        label: wholeOfficeLabel(department.name),
        search: `${wholeOfficeLabel(department.name)} ${department.name}`,
      },
      ...optionsOf(
        teams.filter((team) => team.parentId === department.id),
        department.name,
      ),
    ],
  }));
  const orphans = optionsOf(
    teams.filter((team) => !team.parentId || !known.has(team.parentId)),
    '',
  );
  if (orphans.length) groups.push({ label: 'Chưa thuộc phòng nào', options: orphans });
  return groups.filter((group) => group.options.length > 0);
}

/** Mọi từ gõ vào đều phải có mặt (không cần dấu, không cần đúng thứ tự) — xem `selectOptionMatches`. */
export function teamChoiceMatches(
  choice: { label?: unknown; search?: unknown },
  typed: string,
): boolean {
  return selectOptionMatches(typed, choice);
}

// ---------------------------------------------------------------- danh sách + tìm kiếm

export interface MusicFolderView {
  folder: MusicFolderDto;
  teamLabel: string;
  /** Bài ĐANG HIỆN (đã lọc theo từ khoá), xếp theo tên. */
  tracks: MusicTrackDto[];
  /** Dung lượng của các bài đang hiện. */
  sizeBytes: number;
  /** Số bài thật của thư mục, không phụ thuộc từ khoá. */
  trackCount: number;
}

export interface MusicLibraryView {
  folders: MusicFolderView[];
  /** Ba con số của CẢ kho trong phạm vi người xem — không đổi theo từ khoá. */
  totalTracks: number;
  totalFolders: number;
  totalBytes: number;
  searching: boolean;
  /** Số bài khớp từ khoá (= totalTracks khi không tìm). */
  matchedTracks: number;
}

const sumBytes = (tracks: readonly MusicTrackDto[]) =>
  tracks.reduce((sum, track) => sum + (track.sizeBytes || 0), 0);

/**
 * Tìm theo tên bài, tên thư mục, tên nhóm (không phân biệt dấu). Thư mục hoặc nhóm khớp thì hiện
 * TRỌN thư mục — kể cả khi nó chưa có bài nào; chỉ tên bài khớp thì hiện riêng các bài đó.
 */
export function buildMusicLibrary(
  library: Pick<MusicLibraryResponse, 'folders' | 'tracks'>,
  query: string,
  teamLabel: (teamId: string) => string,
): MusicLibraryView {
  const needle = normalizeForSearch(query);
  const tracksOf = new Map<string, MusicTrackDto[]>();
  for (const track of library.tracks) {
    const list = tracksOf.get(track.folderId);
    if (list) list.push(track);
    else tracksOf.set(track.folderId, [track]);
  }

  const folders: MusicFolderView[] = [];
  for (const folder of library.folders) {
    const label = teamLabel(folder.teamId);
    const all = (tracksOf.get(folder.id) ?? []).sort(
      (a, b) => byName.compare(a.name, b.name) || a.id.localeCompare(b.id),
    );
    const wholeFolder =
      !needle ||
      normalizeForSearch(folder.name).includes(needle) ||
      normalizeForSearch(label).includes(needle);
    const tracks = wholeFolder
      ? all
      : all.filter((track) => normalizeForSearch(track.name).includes(needle));
    if (!wholeFolder && !tracks.length) continue;
    folders.push({
      folder,
      teamLabel: label,
      tracks,
      sizeBytes: sumBytes(tracks),
      trackCount: all.length,
    });
  }
  folders.sort(
    (a, b) =>
      byName.compare(a.folder.name, b.folder.name) || a.folder.id.localeCompare(b.folder.id),
  );

  return {
    folders,
    totalTracks: library.tracks.length,
    totalFolders: library.folders.length,
    totalBytes: sumBytes(library.tracks),
    searching: needle !== '',
    matchedTracks: folders.reduce((sum, view) => sum + view.tracks.length, 0),
  };
}

export interface MusicTeamGroup {
  teamId: string;
  teamLabel: string;
  folders: MusicFolderView[];
  /** Tổng của cả nhóm — để nhìn một cái là biết ngăn nào nặng, không phải cộng nhẩm từng ô. */
  trackCount: number;
  sizeBytes: number;
}

/**
 * Cách sắp thư mục trong một ngăn (26/09/2026): theo TÊN để tìm nhanh, theo SỐ BÀI hoặc DUNG LƯỢNG
 * để thấy ngay ngăn nào đang phình.
 */
export const FOLDER_SORTS = ['name', 'tracks', 'size'] as const;
export type FolderSort = (typeof FOLDER_SORTS)[number];

export function sortFolders(
  folders: readonly MusicFolderView[],
  sort: FolderSort,
): MusicFolderView[] {
  const list = [...folders];
  if (sort === 'tracks') {
    return list.sort(
      (a, b) => b.trackCount - a.trackCount || byName.compare(a.folder.name, b.folder.name),
    );
  }
  if (sort === 'size') {
    return list.sort(
      (a, b) => b.sizeBytes - a.sizeBytes || byName.compare(a.folder.name, b.folder.name),
    );
  }
  return list.sort((a, b) => byName.compare(a.folder.name, b.folder.name));
}

/**
 * Lưới thư mục chia theo NHÓM sở hữu (nhãn «Phòng › Nhóm»), nhóm xếp theo tên; trong nhóm giữ thứ tự
 * thư mục đã sắp. Đúng cách hệ thống cũ chia kho: nhạc của nhóm nào nằm ở ngăn của nhóm đó.
 */
export function groupFoldersByTeam(folders: readonly MusicFolderView[]): MusicTeamGroup[] {
  const groups = new Map<string, MusicTeamGroup>();
  for (const view of folders) {
    const group = groups.get(view.folder.teamId) ?? {
      teamId: view.folder.teamId,
      teamLabel: view.teamLabel,
      folders: [],
      trackCount: 0,
      sizeBytes: 0,
    };
    group.folders.push(view);
    group.trackCount += view.trackCount;
    group.sizeBytes += view.sizeBytes;
    groups.set(view.folder.teamId, group);
  }
  return [...groups.values()].sort(
    (a, b) => byName.compare(a.teamLabel, b.teamLabel) || a.teamId.localeCompare(b.teamId),
  );
}

// ---------------------------------------------------------------- đưa nhạc lên

const EXTENSIONS = Object.keys(MUSIC_MIME_BY_EXTENSION).sort();

/** Thuộc tính `accept` của ô chọn file: mọi kiểu audio + từng đuôi được nhận. */
export const MUSIC_ACCEPT = ['audio/*', ...EXTENSIONS.map((extension) => `.${extension}`)].join(
  ',',
);

/** «.aac .aif .aiff …» — để viết vào lời nhắc. */
export const MUSIC_EXTENSION_LIST = EXTENSIONS.map((extension) => `.${extension}`).join(' ');

/**
 * Soát file TRƯỚC khi gửi — cùng câu chữ với máy chủ (máy chủ vẫn là nơi chặn thật). Gửi trọn một
 * file 300 MB rồi mới bị từ chối là phí vài phút; tệ hơn, máy chủ đóng kết nối giữa chừng nên trình
 * duyệt chỉ báo được «mất kết nối» chứ không nói được lý do.
 */
export function uploadProblem(
  file: { name: string; size: number },
  maxFileBytes: number,
): string | null {
  const dot = file.name.lastIndexOf('.');
  const extension = dot < 0 ? '' : file.name.slice(dot + 1).toLowerCase();
  if (!Object.hasOwn(MUSIC_MIME_BY_EXTENSION, extension)) {
    return `Chỉ nhận file nhạc và .zip (${EXTENSIONS.join(', ')})`;
  }
  if (file.size <= 0) return 'File rỗng';
  if (maxFileBytes > 0 && file.size > maxFileBytes) {
    return `File quá lớn — tối đa ${describeUploadLimit(maxFileBytes)}`;
  }
  return null;
}

/** Tiến độ của CẢ lô: đã xong `index` file, file hiện tại gửi được `fraction` (0 → 1). */
export function batchFraction(index: number, total: number, fraction: number): number {
  if (total <= 0) return 0;
  return Math.min(1, Math.max(0, (index + Math.min(1, Math.max(0, fraction))) / total));
}

export interface UploadOutcome {
  total: number;
  uploaded: number;
  /** «tên file (lý do)» theo thứ tự gặp. */
  failures: readonly string[];
  /** Người dùng bấm dừng giữa chừng. */
  cancelled: boolean;
}

/** Câu báo khi một lô kết thúc. Chỉ nêu lỗi ĐẦU TIÊN — các lỗi sau gần như luôn cùng lý do. */
export function describeUploadOutcome(outcome: UploadOutcome): {
  tone: 'success' | 'warning';
  text: string;
} {
  const { total, uploaded, failures, cancelled } = outcome;
  if (cancelled)
    return { tone: 'warning', text: `Đã dừng — đưa lên được ${uploaded}/${total} bài` };
  if (!failures.length) return { tone: 'success', text: `Đã đưa lên ${uploaded} bài` };
  return {
    tone: 'warning',
    text: `Đưa lên xong ${uploaded}/${total} bài. Lỗi: ${failures[0]}`,
  };
}
